import * as Sentry from '@sentry/react'
import { CLIENT_VERSION } from '@/api/clientInfo'

const dsn = import.meta.env.VITE_SENTRY_DSN

/**
 * Код приглашения в кабинет — секрет: по нему ученику задают пароль и входят
 * в его кабинет. Код стоит в пути публичного API
 * (`/auth/cabinet-invitations/{code}`) и в адресе страницы
 * (`/cabinet/activate?code=…`), а оттуда попадает в крошки xhr и навигации,
 * в спаны трейсинга и в адрес страницы у события.
 */
const SECRET_URL_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/(\/cabinet-invitations\/)[^/?#\s"']+/g, '$1[Filtered]'],
  [/(\/cabinet\/activate\?(?:[^#\s"']*&)?code=)[^&#\s"']+/g, '$1[Filtered]'],
]

export function scrubSecretUrls(value: string): string {
  return SECRET_URL_PATTERNS.reduce((result, [pattern, replacement]) => result.replace(pattern, replacement), value)
}

function scrubStrings(record: Record<string, unknown> | undefined): void {
  if (!record) {
    return
  }
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string') {
      record[key] = scrubSecretUrls(value)
    }
  }
}

interface ScrubbableEvent {
  request?: { url?: string; headers?: Record<string, string> }
  transaction?: string
}

function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  if (event.request?.url) {
    event.request.url = scrubSecretUrls(event.request.url)
  }
  scrubStrings(event.request?.headers)
  if (event.transaction) {
    event.transaction = scrubSecretUrls(event.transaction)
  }

  return event
}

export function initSentry(): void {
  if (!dsn) {
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    // SHA сборки из CI: по нему событие сопоставляется с выкатом
    release: CLIENT_VERSION !== 'dev' ? CLIENT_VERSION : undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
    beforeBreadcrumb: (breadcrumb) => {
      if (breadcrumb.message) {
        breadcrumb.message = scrubSecretUrls(breadcrumb.message)
      }
      scrubStrings(breadcrumb.data)

      return breadcrumb
    },
    beforeSendSpan: (span) => {
      if (span.description) {
        span.description = scrubSecretUrls(span.description)
      }
      scrubStrings(span.data)

      return span
    },
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
  })
}

export { Sentry }
